// ============================================================================
// KLINISOL ILO Platform Page (REDESIGNED)
// Matches Home page design system - Uses Backend API for email
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import { useState } from "react";
import { Link } from 'react-router-dom';
import {
    FileText,
    Shield,
    Mic,
    BarChart3,
    Shuffle,
    Database,
    Archive,
    TrendingUp,
    Lock,
    Layers,
    Brain,
    Zap,
    Award,
    Rocket,
    CheckCircle,
    ArrowRight
} from 'lucide-react';

export default function KLINISOLILOPlatform() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        organization: "",
        message: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.organization) {
            setSubmitStatus('error');
            alert('Please fill in all required fields.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${apiUrl}/contact/ilo-access`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    organization: formData.organization,
                    message: formData.message || 'No message provided'
                })
            });

            if (response.ok) {
                setSubmitStatus('success');
                alert('Thank you for your interest! We\'ll be in touch soon about early access to the KLINiSOL ILO Platform.');
                setFormData({ name: "", email: "", organization: "", message: "" });
            } else {
                throw new Error('Failed to submit');
            }
        } catch (error) {
            console.error('Email sending failed:', error);
            setSubmitStatus('error');
            alert('Sorry, there was an error submitting your request. Please try again or contact us directly at info@klinisol.com');
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-20 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-4">
                        Our ILO Platform
                    </h1>
                    <p className="text-2xl font-semibold mb-2">AI-Driven Clinical Research Without Boundaries</p>
                    <p className="text-xl font-medium mb-4 opacity-90 italic">AI that knows your study, speaks to your team.</p>
                    <p className="text-lg mb-8 max-w-4xl mx-auto opacity-95">
                        ILO (Integrate, Learn, Operate) is a unified, intelligent ecosystem that brings EDC, eTMF, IRT, CTMS, AI Safety Monitoring, and voice-enabled automation together, all designed with <span className="font-bold">21 CFR Part 11 principles</span>.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 mb-8">
                        <button 
                            onClick={() => scrollToSection('access')} 
                            className="px-8 py-4 bg-white text-[#475569] font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg text-lg"
                        >
                            Join the Early Access List
                        </button>
                    </div>

                    {/* Capability Pills */}
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        {["21 CFR Part 11 Compliant", "EHR Integration Ready", "CDISC-Native", "Inspection-Ready by Design"].map((pill, i) => (
                            <span key={i} className="px-4 py-1.5 bg-white/15 border border-white/30 rounded-full text-sm font-medium text-white backdrop-blur-sm">
                                {pill}
                            </span>
                        ))}
                    </div>

                    {/* Pre-launch Banner */}
                    <div className="inline-block bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-xl px-6 py-4">
                        <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full mb-2">
                            <Rocket className="w-5 h-5 text-white" />
                            <span className="text-sm font-bold text-white uppercase tracking-wide">Pre-Launch</span>
                        </div>
                        <p className="text-sm opacity-90">
                            <strong>Pre-launch notice:</strong> The KLIN<span className="text-white font-extrabold">i</span>SOL ILO Platform is currently in advanced development. Pilot deployment begins in 2026.
                        </p>
                    </div>
                </div>
            </section>

            {/* Overview Section */}
            <section id="overview" className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">ILO Overview</h2>
                        <p className="text-lg text-gray-600">
                            Everything clinical research teams need in one secure, modern environment
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                            <p className="text-gray-700 mb-4 leading-relaxed">
                                The <strong>KLIN<span className="text-[#E91E63]">i</span>SOL ILO Platform</strong> unites all critical research functions into a single AI-driven ecosystem. <strong>Smart eCRF Designer</strong>, <strong>AI Safety Management</strong>, <strong>Voice-Enabled Data Capture</strong>, <strong>ePRO/eCOA</strong>, <strong>IRT</strong>, <strong>Centralized Query Manager</strong>, <strong>eTMF</strong>, <strong>CTMS integration</strong>, and designed with <strong>21 CFR Part 11 principles</strong>.
                            </p>
                            <p className="text-gray-700 leading-relaxed">
                                Unlike fragmented legacy systems, ILO is unified, AI-powered, and white-label ready for CROs, enabling faster, smarter, and more compliant studies.
                            </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl border-2 border-[#475569]/20 p-8">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Why It Matters</h3>
                            <ul className="space-y-3">
                                {[
                                    "11 integrated modules — one platform, zero silos",
                                    "AI Safety Monitor with real-time signal detection and ILO verbal alerts",
                                    "Voice-enabled data entry with Silero neural VAD",
                                    "Protocol → eCRF auto-generation in minutes",
                                    "EHR integration ready · CDISC-native · White-label CRO ready"
                                ].map((item, index) => (
                                    <li key={index} className="flex items-start gap-2 text-gray-700">
                                        <CheckCircle className="w-5 h-5 text-[#E91E63] flex-shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Modules Section */}
            <section id="modules" className="py-16 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">Core Modules</h2>
                        <p className="text-lg text-gray-600">
                            Everything you need for modern, compliant studies in one place
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: FileText, title: "Smart eCRF Designer", desc: "AI-generated forms from protocol upload · CDASH/CDISC compliant templates · 385 edit check rules" },
                            { icon: Shield, title: "AI Safety Management", desc: "Real-time AI safety signal monitoring · PV workflows · SAE narratives · 7d/15d expedited reporting" },
                            { icon: Mic, title: "Voice-Enabled Data Capture", desc: "Hands-free eCRF entry powered by Silero VAD + Claude NLU · Clinical terminology optimized" },
                            { icon: BarChart3, title: "ePRO & eCOA", desc: "Patient engagement dashboards and real-time compliance tracking" },
                            { icon: Shuffle, title: "Automated Randomization", desc: "10 scheme types including stratified, adaptive, and minimization · Automated allocation · e-sig unblinding" },
                            { icon: Database, title: "Centralized Query Manager", desc: "Close queries faster with AI suggestions and bulk actions" },
                            { icon: Archive, title: "eTMF", desc: "DIA TMF Reference Model · 12 zones · 98 artifact types · FDA/EMA/MHRA ready" },
                            { icon: TrendingUp, title: "CTMS + Study Metrics Dashboard", desc: "Sites, staff, training, monitoring, CAPA · Live KPIs and enrollment tracking" },
                            { icon: Lock, title: "21 CFR Part 11 Design", desc: "Audit trails, e-signatures with intent capture, RBAC, and real-time compliance scoring" }
                        ].map((module, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-[#E91E63]/50 transition-all"
                            >
                                <module.icon className="w-12 h-12 text-[#E91E63] mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{module.title}</h3>
                                <p className="text-gray-600">{module.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Key Differentiators Section */}
            <section id="differentiators" className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">Key Differentiators</h2>
                        <p className="text-lg text-gray-600">Built for speed, quality, and scale</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        {[
                            { icon: Layers, number: "01", title: "Unified Platform", desc: "One modern stack; no fragile hand-offs or siloed tools" },
                            { icon: Brain, number: "02", title: "AI-Powered", desc: "Predictive analytics, dropout-risk modeling, anomaly detection" },
                            { icon: Mic, number: "03", title: "Voice Integration", desc: "Mobile-first commands, guided visits, and dictation" },
                            { icon: TrendingUp, number: "04", title: "Real-time Dashboards & RBQM", desc: "Continuous risk monitoring and quality by design" },
                            { icon: Zap, number: "05", title: "White-Label Ready", desc: "Brandable for CROs and research networks" }
                        ].map((diff, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#E91E63] transition-all text-center"
                            >
                                <div className="text-5xl font-bold text-[#E91E63] mb-4 opacity-20">{diff.number}</div>
                                <diff.icon className="w-10 h-10 text-[#E91E63] mx-auto mb-3" />
                                <h3 className="text-lg font-bold text-gray-900 mb-2">{diff.title}</h3>
                                <p className="text-sm text-gray-600">{diff.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Proven Results Section */}
            <section id="results" className="py-16 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-3">Target Outcomes</h2>
                        <p className="text-lg text-gray-600">
                            Performance goals ILO is designed to achieve
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        {[
                            { value: "28%", label: "faster query closure" },
                            { value: "45%", label: "reduction in protocol deviation lag" },
                            { value: "22%", label: "improved patient retention" },
                            { value: "40%", label: "faster study startup" },
                            { value: "98.2%", label: "data quality" }
                        ].map((metric, index) => (
                            <div 
                                key={index}
                                className="bg-gradient-to-br from-[#475569]/10 to-[#334155]/20 rounded-xl p-8 text-center border-2 border-[#475569]/30 hover:shadow-lg hover:border-[#E91E63] transition-all"
                            >
                                <Award className="w-8 h-8 text-[#E91E63] mx-auto mb-3" />
                                <div className="text-5xl font-bold text-[#475569] mb-2">{metric.value}</div>
                                <div className="text-sm text-gray-700 font-semibold">{metric.label}</div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Performance Metrics Disclaimer */}
                    <div className="mt-8 max-w-4xl mx-auto bg-white border-2 border-gray-200 p-4 rounded-lg hover:border-[#475569] hover:shadow-md transition-all duration-300">
                        <p className="text-sm text-gray-700 text-center">
                            <strong className="text-[#475569]">Note:</strong> Target performance metrics based on pilot testing and modeled deployments. Full validation pending completion of clinical deployment in 2026.
                        </p>
                    </div>
                </div>
            </section>

            {/* Early Access Section */}
            <section id="access" className="py-16 px-6 bg-gradient-to-r from-[#475569] to-[#E91E63]">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-8 text-white">
                        <Rocket className="w-16 h-16 mx-auto mb-4" />
                        <h2 className="text-4xl font-bold mb-4">Join the Early Access Program</h2>
                        <p className="text-lg opacity-95">
                            Be the first to experience our ILO. Get updates, pilot invitations, and partner pricing for investigator networks and CROs.
                        </p>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border-2 border-white/20">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <input 
                                type="text" 
                                name="name"
                                placeholder="Full name *" 
                                value={formData.name}
                                onChange={handleChange}
                                className="px-4 py-3 rounded-lg border-2 border-white/30 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20"
                                disabled={isSubmitting}
                                required
                            />
                            <input 
                                type="email" 
                                name="email"
                                placeholder="Work email *" 
                                value={formData.email}
                                onChange={handleChange}
                                className="px-4 py-3 rounded-lg border-2 border-white/30 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20"
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        
                        <input 
                            type="text" 
                            name="organization"
                            placeholder="Organization *" 
                            value={formData.organization}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border-2 border-white/30 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 mb-4"
                            disabled={isSubmitting}
                            required
                        />
                        
                        <textarea 
                            name="message"
                            placeholder="What problems would you like ILO to solve?" 
                            value={formData.message}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-4 py-3 rounded-lg border-2 border-white/30 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 mb-4 resize-vertical"
                            disabled={isSubmitting}
                        ></textarea>
                        
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <p className="text-sm text-white/90">
                                By submitting, you agree to be contacted about ILO updates.
                            </p>
                            <button 
                                onClick={handleSubmit} 
                                className="px-8 py-3 bg-white text-[#475569] font-bold rounded-lg hover:bg-gray-100 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Request Access'}
                                {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="text-center md:text-left">
                            <h3 className="text-2xl font-bold mb-1">
                                KLIN<span className="text-white">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                            <Link to="/" className="hover:text-[#E91E63] transition-colors">Home</Link>
                            <span className="opacity-50">·</span>
                            <Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link>
                            <span className="opacity-50">·</span>
                            <Link to="/contact" className="hover:text-[#E91E63] transition-colors">Contact</Link>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 mt-8 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2026 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a 
                                href="https://www.linkedin.com/company/klinisol/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://youtube.com/@klinisol721" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="YouTube"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
